import { demoEvent, demoProspects } from '../data/demo'
import type { ApiEnvelope, EventSummary, Prospect } from '../types/crm'
import { queueMutation, readCache, writeCache } from './offlineDb'

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) { super(message) }
}

async function fetchJson<T>(path: string): Promise<T> {
  const url = `${baseUrl}${path}`
  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new ApiError(`Erreur API (${response.status})`, response.status)
    const value = await response.json() as T
    await writeCache(url, value)
    return value
  } catch (error) {
    const cached = await readCache<T>(url)
    if (cached) return cached
    throw error
  }
}

export async function mutate<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): Promise<T | null> {
  const url = `${baseUrl}${path}`
  try {
    const response = await fetch(url, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({ error: "Impossible d'enregistrer la modification." })) as { error?: string }
      throw new ApiError(payload.error ?? "Impossible d'enregistrer la modification.", response.status)
    }
    return await response.json() as T
  } catch (error) {
    if (!navigator.onLine || (!(error instanceof ApiError) && path.startsWith('/api/'))) {
      await queueMutation({ id: crypto.randomUUID(), url, method, body, createdAt: new Date().toISOString() })
      return null
    }
    throw error
  }
}

export async function getEvents(): Promise<EventSummary[]> {
  try {
    return (await fetchJson<ApiEnvelope<EventSummary[]>>('/api/events')).data
  } catch {
    return [demoEvent]
  }
}

export async function getEventProspects(eventId: string): Promise<Prospect[]> {
  try {
    const envelope = await fetchJson<ApiEnvelope<Prospect[]>>(`/api/events/${encodeURIComponent(eventId)}/companies?limit=250`)
    return envelope.data.length ? envelope.data : demoProspects
  } catch {
    return demoProspects
  }
}

export async function getCompany(companyId: string, eventId: string): Promise<Prospect> {
  try {
    return (await fetchJson<ApiEnvelope<Prospect>>(`/api/companies/${encodeURIComponent(companyId)}?eventId=${encodeURIComponent(eventId)}`)).data
  } catch {
    const company = demoProspects.find((item) => item.id === companyId)
    if (!company) throw new ApiError('Société introuvable.', 404)
    return company
  }
}
